import { useCallback, useEffect, useState } from 'react';

import { CustomerForm } from './CustomerForm.tsx';
import { NativeSelect } from './NativeSelect.tsx';
import { StatusMessage } from './StatusMessage.tsx';
import { useCounterparties, useCreateCounterparty } from '../lib/counterparties.ts';
import {
  CUSTOMER_LOAD_ERROR,
  CUSTOMER_SAVE_ERROR,
  type Counterparty,
  type UpsertCounterpartyInput,
} from '../../shared/counterparty.ts';
import type { InvoiceSnapshot } from '../../shared/invoice.ts';

export type CustomerPickerValue = Pick<Counterparty, 'id' | 'name' | 'email' | 'billTo' | 'shipTo'>;

export interface CustomerPickerProps {
  id: string;
  label: 'Bill To' | 'Ship To' | string;
  value: CustomerPickerValue | null;
  addressKind?: 'billTo' | 'shipTo';
  preservedSnapshot?: InvoiceSnapshot | null;
  disabled?: boolean;
  onChange: (customer: Counterparty | null) => void;
}

const CREATE_OPTION_VALUE = '__create_customer__';
const SNAPSHOT_OPTION_VALUE = '__preserved_snapshot__';
type MobilePickerPanel = 'closed' | 'select' | 'create';

export function CustomerPicker({
  id,
  label,
  value,
  addressKind = 'billTo',
  preservedSnapshot = null,
  disabled,
  onChange,
}: CustomerPickerProps): React.ReactElement {
  const [creating, setCreating] = useState(false);
  const [mobilePanel, setMobilePanel] = useState<MobilePickerPanel>('closed');
  const [mobileSearch, setMobileSearch] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const customersQuery = useCounterparties('');
  const mobileCustomersQuery = useCounterparties(mobileSearch);
  const createCustomer = useCreateCounterparty();
  const createCustomerPending = createCustomer.isPending;
  const createCustomerMutateAsync = createCustomer.mutateAsync;
  const createCustomerReset = createCustomer.reset;
  const customers = customersQuery.data?.counterparties ?? [];
  const mobileCustomers = mobileCustomersQuery.data?.counterparties ?? [];
  const selectValue = value?.id ?? (preservedSnapshot ? SNAPSHOT_OPTION_VALUE : '');
  const selectedCustomerMissing =
    value !== null && !customers.some((customer) => customer.id === value.id);
  const selectedCustomerMissingFromMobileSearch =
    value !== null && !mobileCustomers.some((customer) => customer.id === value.id);

  const closeCreateDialog = useCallback((): void => {
    setSaveError(null);
    createCustomerReset();
    if (mobilePanel === 'create') {
      setMobilePanel('select');
      return;
    }
    setCreating(false);
  }, [createCustomerReset, mobilePanel]);

  const closeMobilePanel = useCallback((): void => {
    setMobilePanel('closed');
    setSaveError(null);
    setMobileSearch('');
    createCustomerReset();
  }, [createCustomerReset]);

  useEffect(() => {
    if (!creating && mobilePanel === 'closed') return;

    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !createCustomerPending) {
        if (mobilePanel === 'select') {
          closeMobilePanel();
        } else {
          closeCreateDialog();
        }
      }
    }

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [creating, mobilePanel, createCustomerPending, closeCreateDialog, closeMobilePanel]);

  async function saveCustomer(input: UpsertCounterpartyInput): Promise<void> {
    setSaveError(null);
    try {
      const response = await createCustomerMutateAsync(input);
      onChange(response.counterparty);
      setSaveMessage('Customer saved.');
      setCreating(false);
      closeMobilePanel();
    } catch {
      setSaveError(CUSTOMER_SAVE_ERROR);
    }
  }

  function changeSelection(nextValue: string): void {
    setSaveMessage(null);

    if (nextValue === CREATE_OPTION_VALUE) {
      setSaveError(null);
      setCreating(true);
      return;
    }

    if (nextValue === SNAPSHOT_OPTION_VALUE) return;

    if (nextValue === '') {
      onChange(null);
      return;
    }

    const customer = customers.find((candidate) => candidate.id === nextValue);
    if (customer) onChange(customer);
  }

  return (
    <section className="w-full min-w-0 max-w-full space-y-2">
      <div className="space-y-1.5">
        <label
          id={`${id}-label`}
          htmlFor={`${id}-select`}
          className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-muted"
        >
          {label}
        </label>
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="dialog"
          onClick={() => setMobilePanel('select')}
          className="inline-flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border border-line bg-paper-soft px-3 py-2 text-left text-sm text-ink shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] disabled:cursor-not-allowed disabled:bg-paper-deep disabled:text-ink-muted sm:hidden"
        >
          <span className="min-w-0 truncate">
            {value?.name ?? preservedSnapshot?.name ?? 'Select customer'}
          </span>
          <span
            aria-hidden="true"
            className="shrink-0 text-xs uppercase tracking-[0.16em] text-ink-muted"
          >
            Select
          </span>
        </button>
        <NativeSelect
          id={`${id}-select`}
          value={selectValue}
          disabled={disabled}
          onChange={(event) => changeSelection(event.currentTarget.value)}
          className="min-w-0 max-w-full disabled:text-ink-muted"
          wrapperClassName="hidden min-w-0 sm:block"
        >
          <option value="">Select customer</option>
          {preservedSnapshot && !value ? (
            <option value={SNAPSHOT_OPTION_VALUE}>
              Keeping deleted snapshot: {preservedSnapshot.name}
            </option>
          ) : null}
          {selectedCustomerMissing && value ? <option value={value.id}>{value.name}</option> : null}
          {customers.map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.email ? `${customer.name} (${customer.email})` : customer.name}
            </option>
          ))}
          {customers.length === 0 && customersQuery.isSuccess ? (
            <option value="__empty_customers__" disabled>
              No customers saved
            </option>
          ) : null}
          <option value={CREATE_OPTION_VALUE}>Create new customer</option>
        </NativeSelect>
      </div>

      {saveMessage ? <StatusMessage tone="success">{saveMessage}</StatusMessage> : null}

      <CustomerDetails
        customer={value}
        preservedSnapshot={preservedSnapshot}
        addressKind={addressKind}
      />

      {customersQuery.isPending ? (
        <p className="text-sm text-ink-muted">Loading customers...</p>
      ) : null}
      {customersQuery.isError ? (
        <p role="alert" className="text-sm text-clay">
          {CUSTOMER_LOAD_ERROR}
        </p>
      ) : null}

      {creating ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${id}-dialog-title`}
          className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden bg-ink/40 px-4 py-6 backdrop-blur-sm"
        >
          <div className="max-h-full w-full min-w-0 max-w-xl overflow-auto overflow-x-hidden rounded-2xl border border-line bg-paper-soft p-6 shadow-[0_30px_80px_-30px_rgba(31,27,21,0.6)]">
            <h2
              id={`${id}-dialog-title`}
              className="mb-5 font-serif text-2xl italic leading-tight text-ink"
            >
              New Customer
            </h2>
            {saveError ? <StatusMessage tone="error">{saveError}</StatusMessage> : null}
            <CustomerForm
              submitLabel="Save"
              submitting={createCustomerPending}
              onCancel={closeCreateDialog}
              onSubmit={saveCustomer}
            />
          </div>
        </div>
      ) : null}

      {mobilePanel === 'select' ? (
        <MobileCustomerPickerDialog
          id={id}
          label={label}
          search={mobileSearch}
          value={value}
          preservedSnapshot={preservedSnapshot}
          selectedCustomerMissing={selectedCustomerMissingFromMobileSearch}
          customers={mobileCustomers}
          loading={mobileCustomersQuery.isPending}
          error={mobileCustomersQuery.isError}
          onSearchChange={setMobileSearch}
          onClose={closeMobilePanel}
          onCreate={() => {
            setSaveError(null);
            setMobilePanel('create');
          }}
          onSelect={(customer) => {
            setSaveMessage(null);
            onChange(customer);
            closeMobilePanel();
          }}
          onClear={() => {
            setSaveMessage(null);
            onChange(null);
            closeMobilePanel();
          }}
        />
      ) : null}

      {mobilePanel === 'create' ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${id}-mobile-create-title`}
          className="fixed inset-0 z-50 flex min-w-0 max-w-full flex-col overflow-x-hidden bg-paper"
        >
          <div className="flex min-h-14 min-w-0 items-center justify-between gap-3 border-b border-line bg-paper-soft px-4 py-3">
            <h2
              id={`${id}-mobile-create-title`}
              className="min-w-0 break-words font-serif text-xl italic leading-tight text-ink"
            >
              New Customer
            </h2>
            <button
              type="button"
              disabled={createCustomerPending}
              onClick={closeCreateDialog}
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-paper px-3.5 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
          <div className="min-h-0 min-w-0 flex-1 overflow-auto overflow-x-hidden px-4 py-5">
            {saveError ? <StatusMessage tone="error">{saveError}</StatusMessage> : null}
            <CustomerForm
              submitLabel="Save"
              submitting={createCustomerPending}
              onCancel={closeCreateDialog}
              onSubmit={saveCustomer}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}

function MobileCustomerPickerDialog({
  id,
  label,
  search,
  value,
  preservedSnapshot,
  selectedCustomerMissing,
  customers,
  loading,
  error,
  onSearchChange,
  onClose,
  onCreate,
  onSelect,
  onClear,
}: {
  id: string;
  label: React.ReactNode;
  search: string;
  value: CustomerPickerValue | null;
  preservedSnapshot: InvoiceSnapshot | null;
  selectedCustomerMissing: boolean;
  customers: Counterparty[];
  loading: boolean;
  error: boolean;
  onSearchChange: (value: string) => void;
  onClose: () => void;
  onCreate: () => void;
  onSelect: (customer: Counterparty) => void;
  onClear: () => void;
}): React.ReactElement {
  const hasSearch = search.trim() !== '';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${id}-mobile-picker-title`}
      className="fixed inset-0 z-50 flex min-w-0 max-w-full flex-col overflow-x-hidden bg-paper"
    >
      <div className="min-w-0 space-y-3 border-b border-line bg-paper-soft px-4 py-3">
        <div className="flex min-h-11 min-w-0 items-center justify-between gap-3">
          <h2
            id={`${id}-mobile-picker-title`}
            className="min-w-0 break-words font-serif text-xl italic leading-tight text-ink"
          >
            {label}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-paper px-3.5 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep"
          >
            Close
          </button>
        </div>
        <label className="sr-only" htmlFor={`${id}-mobile-search`}>
          Search customers
        </label>
        <input
          id={`${id}-mobile-search`}
          type="search"
          value={search}
          autoFocus
          placeholder="Search customers"
          onChange={(event) => onSearchChange(event.currentTarget.value)}
          className={mobileSearchInputClassName}
        />
      </div>
      <div className="min-h-0 min-w-0 flex-1 overflow-auto overflow-x-hidden px-4 py-4">
        <div className="min-w-0 space-y-3">
          {value && selectedCustomerMissing ? (
            <div role="listbox" aria-label={`${label} selected customer`}>
              <MobileCustomerOption customer={value} selected onSelect={() => onClose()} />
            </div>
          ) : null}
          {preservedSnapshot && !value ? (
            <div className="rounded-xl border border-line bg-paper-deep/50 p-3 text-sm text-ink-soft">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-muted">
                Keeping deleted snapshot
              </p>
              <p className="mt-1 font-serif text-base italic text-ink">{preservedSnapshot.name}</p>
            </div>
          ) : null}
          <button
            type="button"
            onClick={onClear}
            className="inline-flex min-h-11 w-full items-center justify-center rounded-full border border-line bg-paper px-4 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep"
          >
            Clear selection
          </button>
          {loading ? <p className="text-sm text-ink-muted">Loading...</p> : null}
          {error ? (
            <p role="alert" className="text-sm text-clay">
              {CUSTOMER_LOAD_ERROR}
            </p>
          ) : null}
          {!loading && !error && customers.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {hasSearch ? 'No matching customers' : 'No customers saved'}
            </p>
          ) : null}
          {customers.length > 0 ? (
            <div role="listbox" aria-label={`${label} customers`} className="space-y-3">
              {customers.map((customer) => (
                <MobileCustomerOption
                  key={customer.id}
                  customer={customer}
                  selected={value?.id === customer.id}
                  onSelect={() => onSelect(customer)}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="border-t border-line bg-paper-soft px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex min-h-11 w-full items-center justify-center rounded-full bg-ochre px-4 text-sm font-semibold text-paper-soft shadow-[0_8px_20px_-12px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep"
        >
          Create new customer
        </button>
      </div>
    </div>
  );
}

function MobileCustomerOption({
  customer,
  selected,
  onSelect,
}: {
  customer: CustomerPickerValue;
  selected: boolean;
  onSelect: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      data-picker-option
      onClick={onSelect}
      className={`block min-h-11 w-full rounded-xl border px-3 py-3 text-left text-sm transition ${selected ? 'border-ochre/60 bg-ochre/10 text-ink' : 'border-line bg-paper text-ink-soft hover:border-ochre/40 hover:bg-paper-deep'}`}
    >
      <span className="block break-words font-serif text-base italic leading-tight text-ink">
        {customer.name}
      </span>
      {customer.email ? <span className="mt-1 block break-words">{customer.email}</span> : null}
    </button>
  );
}

function CustomerDetails({
  customer,
  preservedSnapshot,
  addressKind,
}: {
  customer: CustomerPickerValue | null;
  preservedSnapshot: InvoiceSnapshot | null;
  addressKind: 'billTo' | 'shipTo';
}): React.ReactElement {
  const snapshot = customer ? snapshotFromCustomer(customer) : preservedSnapshot;
  if (!snapshot) return <p className="text-sm italic text-ink-muted">Select customer.</p>;

  const addressLines = splitOptionalLines(snapshot[addressKind]);

  return (
    <div className="min-h-24 w-full min-w-0 max-w-full break-words rounded-xl border border-line bg-paper-soft/70 px-4 py-3 text-sm leading-6 text-ink-soft [overflow-wrap:anywhere]">
      {!customer ? (
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-muted">
          Keeping deleted snapshot
        </p>
      ) : null}
      <p className="font-serif text-base italic leading-tight text-ink">{snapshot.name}</p>
      {snapshot.email ? <p className="mt-1">{snapshot.email}</p> : null}
      {addressLines.map((line, index) => (
        <p key={`${line}-${index}`} className="mt-0.5">
          {line}
        </p>
      ))}
    </div>
  );
}

function snapshotFromCustomer(customer: CustomerPickerValue): InvoiceSnapshot {
  return {
    name: customer.name,
    email: customer.email,
    billTo: customer.billTo,
    shipTo: customer.shipTo,
  };
}

function splitOptionalLines(value: string | null): string[] {
  if (value == null || value.trim() === '') return [];
  return value
    .trim()
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

const mobileSearchInputClassName =
  'block min-h-11 w-full min-w-0 max-w-full rounded-lg border border-line bg-paper-soft px-3 py-2 text-sm text-ink shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] outline-none transition placeholder:text-ink-muted focus:border-ochre focus:ring-2 focus:ring-ochre/30 disabled:cursor-not-allowed disabled:bg-paper-deep disabled:text-ink-muted';
